import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import {ReorderableHandle} from './Reorderable';
import { useCallback } from 'react';
import { DAYS_OF_WEEK } from './pages/LandingPages/data/constants';
import { generateRange, titlecase } from 'lib/utils';
import FreeTextMultiSelect from './UI/FreeTextMultiSelect/FreeTextMultiSelect';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

const OPERATORS = {
  'in': 'is one of the following:',
  'not_in': 'is not any of the following:',
  'less_than': 'is less than',
  'greater_than': 'is greater than',
};

export type Operator = keyof typeof OPERATORS

export interface Option {
  text: string
  value: string
}

export interface RuleProps<EntityId extends string | number> {
  entityName: string
  entityId: EntityId
  setEntityId: (v: string) => unknown
  entities: Record<EntityId, string>
  entityOptions?: Option[]
  operator: Operator
  setOperator: (o: Operator) => unknown
  value: string[]
  setValue: (v: string[]) => unknown
  canBeEmpty?: boolean
  reorderable?: boolean
  onRemove?: () => void
  permitEdit?: boolean
  dayOfWeek: string
  setDayOfWeek: (v: string | null) => unknown
  startTimeOfDay: string
  setStartTimeOfDay: (v: string | null) => unknown
  endTimeOfDay: string
  setEndTimeOfDay: (v: string | null) => unknown
}

export default function Rule<EntityId extends string | number>({
  entityName,
  entityId,
  setEntityId,
  entities,
  entityOptions,
  operator,
  setOperator,
  value,
  setValue,
  canBeEmpty = false,
  reorderable = false,
  onRemove,
  permitEdit = true,
  dayOfWeek,
  setDayOfWeek,
  startTimeOfDay,
  setStartTimeOfDay,
  endTimeOfDay,
  setEndTimeOfDay,
} : RuleProps<EntityId>) {
  if(!Array.isArray(value)) {
    value = []
  }

  const addTimeConstraint = useCallback(() => {
    setDayOfWeek('sunday')
    setStartTimeOfDay('00:00')
    setEndTimeOfDay('23:59')
  }, [setDayOfWeek, setStartTimeOfDay,setEndTimeOfDay])

  const removeTimeCondition = useCallback(() => {
    setDayOfWeek(null)
    setStartTimeOfDay(null)
    setEndTimeOfDay(null)
  }, [setDayOfWeek, setStartTimeOfDay,setEndTimeOfDay])

  return (
    <Stack spacing={2} role="listitem" aria-roledescription="exclusion rule">
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={{ height: '48px', alignContent: 'center'}}>
          {reorderable && permitEdit && <ReorderableHandle/>}

          {onRemove && permitEdit && (
            <IconButton onClick={onRemove} size="large">
              <RemoveCircleIcon/>
            </IconButton>
          )}

          <IconButton onClick={addTimeConstraint} size="large" disabled={!permitEdit || (dayOfWeek?.length > 0)}>
            <MoreTimeIcon/>
          </IconButton>
        </Box>

        <TextField
          sx={{ flex: 1, minWidth: 180 }}
          select
          InputLabelProps={{ shrink: true }}
          label={entityName}
          value={entityId || ''}
          onChange={(event: { target: { value: string } }) => setEntityId(event.target.value)}
          SelectProps={{ displayEmpty: canBeEmpty }}
          disabled={!permitEdit}
        >
          {canBeEmpty && <MenuItem value="">None</MenuItem>}
          {Object.keys(entities).sort().map(id => (
            <MenuItem value={id} key={id}>{entities[id]}</MenuItem>
          ))}
        </TextField>

        <TextField
          sx={{ flex: 1, minWidth: 180 }}
          select
          InputLabelProps={{ shrink: true }}
          label="Operator"
          value={operator || ''}
          onChange={event => setOperator(event.target.value as Operator)}
          disabled={!permitEdit}
        >
          {Object.entries(OPERATORS).map(([operator, description]) => (
            <MenuItem value={operator} key={operator}>{description}</MenuItem>
          ))}
        </TextField>

        {(operator === 'in' || operator === 'not_in') ? (
          <FreeTextMultiSelect
            label="Value"
            value={value}
            setValue={setValue}
            disabled={!permitEdit}
            options={entityOptions ? entityOptions.map(e => e.value) : []}
            required={true}
            sx={{ flex: 1 }}
          />
        ) : (
          <TextField
            label="Value"
            value={value[0] || ''}
            onChange={(event) => setValue([event.target.value])}
            disabled={!permitEdit}
            required={true}
            sx={{ flex: 1 }}
          />
        )}

      </Stack>

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        aria-roledescription="exclusion rule time constraint"
        sx={{ pl: 9 }}
      >
        {permitEdit && dayOfWeek && (
          <>
            <IconButton onClick={removeTimeCondition} size="large">
              <RemoveCircleIcon/>
            </IconButton>

            <TextField
              sx={{ flex: 1, minWidth: 180 }}
              select
              InputLabelProps={{ shrink: true }}
              label="Day Of Week"
              value={dayOfWeek || ''}
              onChange={event => setDayOfWeek(event.target.value as string)}
              disabled={!permitEdit}
            >
              {DAYS_OF_WEEK.map((day) => (
                <MenuItem value={day} key={day}>{titlecase(day)}</MenuItem>
              ))}
            </TextField>

            <TextField
              sx={{ flex: 1, minWidth: 180 }}
              select
              InputLabelProps={{ shrink: true }}
              label="Start Time"
              value={startTimeOfDay || ''}
              onChange={event => setStartTimeOfDay(event.target.value as string)}
              disabled={!permitEdit}
            >
              {[...generateRange(0,23)]
                .map(n => n.toString().padStart(2, '0').concat(':00'))
                .map((startTime) => (
                  <MenuItem value={startTime} key={startTime}>{startTime}</MenuItem>
                ))}
            </TextField>

            <TextField
              sx={{ flex: 1, minWidth: 180 }}
              select
              InputLabelProps={{ shrink: true }}
              label="End Time"
              value={endTimeOfDay || ''}
              onChange={event => setEndTimeOfDay(event.target.value as string)}
              disabled={!permitEdit}
            >
              {[...generateRange(0,23)]
                .map(n => n.toString().padStart(2, '0').concat(':59'))
                .map((endTime) => (
                  <MenuItem value={endTime} key={endTime}>{endTime}</MenuItem>
                ))}
            </TextField>
          </>
        )}
      </Stack>
    </Stack>
  )
}
